import React, { useState, useMemo, useContext } from 'react';

import { Tabs, List } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { MODEL_AGENCY_TYPES, MODEL_AGENCY_TYPE_ORDERS } from '@constants/ModelAgencyTypes';

import { UserContext } from '@contexts/UserContext';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import { useContactAgencies, useRemoveAgency } from '@hooks/API/Contact/UseContactAgencies';
import { forceReloadModels, getModel, modelFullName } from '@hooks/API/Contact/UseContactsList';

import CustomerDrawer from '@components/ContactDetails/CustomerDetails/Drawer/CustomerDrawer';
import CommissionsDrawer from '@components/ContactsCommissionsDrawers/CommissionsDrawer';
import EmptyState from '@components/Gui/EmptyState';
import { NwAddButton, NwButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import ModelAgenciesDrawer from '@components/ModelAgencies/ModelAgenciesDrawer';
import ModelAgenciesMap from '@components/ModelAgencies/ModelAgenciesMap';

import ModelAgenciesListItem from './ModelAgenciesListItem';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const StyledCard = styled(NwCard)`
    .ant-card-body {
        .ant-tabs {
            .ant-tabs-nav {
                margin: 0;
                padding: 0 2rem;

                .ant-tabs-nav-wrap {
                    .ant-tabs-nav-list {
                        .ant-tabs-tab {
                            font-size: ${(props) => props.theme.FontSizeSm};
                            text-transform: uppercase;
                            padding: 0.5rem 0;
                        }
                    }
                }
            }
        }
    }
`;

const ButtonsContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;

const ModelAgenciesCard = ({ contactID, represented, drawerMode }) => {
    const { data: currentContactAgencies } = useContactAgencies(contactID);
    const [viewMode, setViewMode] = useState('LISTING'); // LISTING, INSERTING, EDITING
    const [currentModelAgency, setCurrentModelAgency] = useState(null);
    const [showMap, setShowMap] = useState(false);
    const [selectedType, setSelectedType] = useState('All');
    const [selectedCommissionsAgency, setSelectedCommissionsAgency] = useState(null);
    const [previewAgency, setPreviewAgency] = useState(null);

    const { currentUser } = useContext(UserContext);
    const { nwAgId } = currentUser.ActiveContext;

    const { mutate: onRemoveAgency } = useRemoveAgency();

    let motherExist = false;
    let exclusions = [];
    if (currentContactAgencies) {
        currentContactAgencies.forEach((el) => {
            if (el.TypeName === 'Mother' && !el.Dropped) motherExist = true;
            if (!el.Dropped) {
                exclusions.push(el.AgencyID);
            }
        });
    }

    const handleCloseDrawer = () => {
        setCurrentModelAgency(null);
        setViewMode('LISTING');
    };

    const handleEditModelAgency = (modelAgency) => {
        setCurrentModelAgency(modelAgency);
        setViewMode('EDITING');
    };

    const handleRemoveModelAgency = (modelAgency) => {
        onRemoveAgency({ ModelAgencyID: modelAgency.ID, AgencyID: modelAgency.AgencyID, ModelID: contactID });
    };

    const currentModel = getModel(contactID);

    const setAsRepresented = async () => {
        await Axios.post('/models/setRepresented', { ModelID: contactID, Represented: true, NetworkAgencyID: nwAgId });
        forceReloadModels();
        forceReloadContact(contactID);
    };

    const setAsNotRepresented = async () => {
        await Axios.post('/models/setRepresented', { ModelID: contactID, Represented: false, NetworkAgencyID: nwAgId });
        forceReloadModels();
        forceReloadContact(contactID);
    };

    const modelAgencies = useMemo(() => {
        let modelAgenciesList = [];
        if (currentContactAgencies) {
            modelAgenciesList = currentContactAgencies.filter((modelagency) => {
                if (modelagency.TypeName === selectedType || selectedType === 'All') {
                    return true;
                }
                return false;
            });
            modelAgenciesList.sort((modelagency1, modelagency2) =>
                modelagency1.AgencyName > modelagency2.AgencyName ? 1 : -1
            );
            modelAgenciesList.sort((modelagency1, modelagency2) =>
                MODEL_AGENCY_TYPE_ORDERS[modelagency1.TypeName] >= MODEL_AGENCY_TYPE_ORDERS[modelagency2.TypeName]
                    ? 1
                    : -1
            );
            modelAgenciesList.sort((modelagency1, modelagency2) => modelagency1.Dropped - modelagency2.Dropped);
        }
        return modelAgenciesList;
    }, [currentContactAgencies, selectedType]);

    const onChangeTab = (key) => {
        setSelectedType(key);
    };

    const handleShowMap = () => {
        setShowMap(true);
    };

    const handleHideMap = () => {
        setShowMap(false);
    };

    return (
        <>
            <StyledCard
                title="Model Agencies"
                icon={byPrefixAndName.fasl['globe']}
                fullheight
                extra={
                    <ButtonsContainer>
                        {/* {motherExist
                            ?
                            <NwIcon style={{marginRight: ".5rem", cursor: "not-allowed"}} disabled icon={byPrefixAndName.fasl['medal']} tooltip="There's a mother agency, you can't set this model as represented" />
                            : */}
                        {represented ? (
                            <NwButton
                                ghost
                                icon={byPrefixAndName.fasl['handshake-slash']}
                                label="set as not represented"
                                tooltip="Set this model as not represented"
                                onClick={setAsNotRepresented}
                            />
                        ) : (
                            <NwButton
                                ghost
                                icon={byPrefixAndName.fasl['medal']}
                                label="set as represented"
                                tooltip="Set this model as represented"
                                onClick={setAsRepresented}
                            />
                        )}
                        {/* } */}
                        <NwButton
                            ghost
                            icon={byPrefixAndName.fasl['map-location-dot']}
                            label="map"
                            onClick={handleShowMap}
                        />
                        <NwAddButton
                            className="add-button"
                            ghost
                            onClick={() => {
                                setViewMode('INSERTING');
                            }}
                            label="Add"
                        />
                    </ButtonsContainer>
                }
                padding="no"
            >
                <Tabs
                    defaultActiveKey={selectedType}
                    onChange={onChangeTab}
                    items={MODEL_AGENCY_TYPES.map((modagtype, tabIndex) => {
                        return {
                            key: modagtype.value,
                            label: modagtype.value,
                        };
                    })}
                />
                {modelAgencies.length === 0 ? (
                    <EmptyState message="no agencies" style={{ marginTop: '2.8rem' }} />
                ) : (
                    <List
                        itemLayout="horizontal"
                        dataSource={modelAgencies}
                        renderItem={(item) => {
                            return (
                                <ModelAgenciesListItem
                                    modelAgency={item}
                                    handleEdit={handleEditModelAgency}
                                    handleRemove={handleRemoveModelAgency}
                                    handleCommissions={setSelectedCommissionsAgency}
                                    onNameClick={() => setPreviewAgency(item.AgencyID)}
                                />
                            );
                        }}
                    />
                )}
            </StyledCard>
            {showMap && <ModelAgenciesMap agencies={currentContactAgencies} onClose={handleHideMap} />}
            {(viewMode === 'INSERTING' || viewMode === 'EDITING') && (
                <ModelAgenciesDrawer
                    viewMode={viewMode}
                    exclusions={exclusions}
                    motherExist={motherExist}
                    contactID={contactID}
                    currentModelAgency={currentModelAgency}
                    onClose={handleCloseDrawer}
                    represented={represented}
                />
            )}
            {previewAgency && <CustomerDrawer customerID={previewAgency} onClose={() => setPreviewAgency(null)} />}
            {selectedCommissionsAgency && (
                <CommissionsDrawer
                    agencyId={selectedCommissionsAgency.AgencyID}
                    agencyName={selectedCommissionsAgency.AgencyName}
                    modelId={contactID}
                    modelName={currentModel ? modelFullName(currentModel) : undefined}
                    mode={
                        selectedCommissionsAgency.TypeName?.toLowerCase() === 'placement'
                            ? 'asMotherAgency'
                            : 'toMotherAgency'
                    }
                    onClose={() => setSelectedCommissionsAgency(null)}
                />
            )}
        </>
    );
};

export default ModelAgenciesCard;
